/**
 * Az Android vissza gomb — a natív `AppBack` plugin burkolója.
 *
 * A gomb NEM navigálhat magától: a natív oldal minden megnyomást átad ide,
 * és a felület dönti el, mit jelent (lásd `src/hooks/useBackHandler.ts`).
 *
 * iOS-en nincs vissza gomb, a plugin sem létezik — ezért minden hívás
 * csendben elszáll a `Capacitor.isNativePlatform()` kapun. Böngészőben
 * ugyanígy: ott a böngésző saját vissza gombja a `history`-t viszi, amibe
 * nem szólunk bele.
 */

import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';

interface AppBackPlugin {
  /**
   * Átvegye-e a felület a vissza gombot.
   *
   * ⚠️ Amíg `false`, a RENDSZER alapértelmezése érvényes. A natív oldal
   * szándékosan így indul: ha a webréteg elszállna vagy lassan töltene be,
   * a felhasználó akkor se ragadjon bent az appban.
   */
  setEnabled(options: { enabled: boolean }): Promise<void>;
  /** Kilépés — akkor hívjuk, ha a felületnek nincs hova visszalépnie. */
  exitApp(): Promise<void>;
  addListener(
    eventName: 'backButton',
    listener: () => void,
  ): Promise<PluginListenerHandle>;
}

const AppBack = registerPlugin<AppBackPlugin>('AppBack');

/** Csak Androidon van hardveres/gesztusos vissza gomb. */
export function backButtonAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

/**
 * Feliratkozás a natív vissza gombra.
 *
 * @returns Leiratkozó függvény. Nem-Android platformon no-op.
 */
export async function onNativeBack(handler: () => void): Promise<() => void> {
  if (!backButtonAvailable()) return () => {};

  const listener = await AppBack.addListener('backButton', handler);
  // A feliratkozás UTÁN engedélyezzük, nem előtte: fordított sorrendben egy
  // gyorsan érkező megnyomás senkihez sem jutna el, és elnyelnénk.
  await AppBack.setEnabled({ enabled: true });

  return () => {
    void AppBack.setEnabled({ enabled: false });
    void listener.remove();
  };
}

/** Kilépés az appból. Nem-Android platformon nem csinál semmit. */
export async function exitApp(): Promise<void> {
  if (!backButtonAvailable()) return;
  await AppBack.exitApp();
}
